import React, { useEffect } from 'react';
import { Card, Divider, Row, Col, Space, Typography } from 'antd';
import { RightOutlined, AudioOutlined } from '@ant-design/icons';

/**
 * Generalized ContentNavigator Component.
 *
 * This component is a reusable navigation card that allows for rendering dynamic sections,
 * a top action button, and a list of links (e.g., RSS links).
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {React.ReactNode} props.topAction - A React node representing the top action button (e.g., 'Create an SMA' button).
 * @param {Array} props.sectionsConfig - Configuration array for dynamic sections. Each section contains:
 *   - {string} title - The title of the section.
 *   - {Array} items - Array of items in the section, each item contains:
 *     - {string} title - The title of the item.
 *     - {string} [text] - Optional descriptive text for the item.
 *     - {function} onClick - Callback function when the item is clicked.
 * @param {Array} [props.linksConfig] - Configuration array for links. Each link contains:
 *   - {string} label - The label for the link.
 *   - {function} onClick - Callback function when the link is clicked.
 *
 * @returns {React.ReactElement} The rendered ContentNavigator component.
 */
const ContentNavigator = ({ topAction, sectionsConfig, linksConfig }) => {
    return (
        <Card className={'gapless-y'} style={{ width: 300, backgroundColor: '#fff' }} bordered={false}>
            {/* Top Action Button (e.g., Create an SMA) */}
            {topAction && (
                <div style={{ marginBottom: '15px' }}>
                    {topAction}
                </div>
            )}
            <Divider />

            {/* Dynamic Sections */}
            {sectionsConfig.map((section, sectionIndex) => (
                <div key={sectionIndex} style={{ marginBottom: '20px' }}>
                    {/* Section Title */}
                    <div className={'round-2'} style={{ fontWeight: '400', marginBottom: '5px', color: '#1c75bc', backgroundColor: '#e6f7ff', padding: '5px 10px' }}>
                        {section.title}
                    </div>

                    {/* Section Items */}
                    {section.items.map((item, itemIndex) => (
                        <div
                            key={itemIndex}
                            style={{ padding: '5px 10px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#ffffff', marginBottom: '5px', borderRadius: '4px', borderBottom: '1px dashed #ddd' }}
                            onClick={() => item.onClick()}
                        >
                            <Space direction={'vertical'}>
                                <Typography.Text style={{ fontSize: '16px', fontWeight: '500' }} strong>{item.title}</Typography.Text>
                                {item.text && (
                                    <Typography.Paragraph ellipsis={{ rows: 2 }} style={{ fontSize: '13px' }}>{item.text}</Typography.Paragraph>
                                )}
                            </Space>
                            <RightOutlined style={{ fontSize: '12px', color: '#8c8c8c' }} />
                        </div>
                    ))}
                </div>
            ))}

            {/* Links */}
            {linksConfig && (
                <Space direction={'vertical'}>
                    {linksConfig.map((link, linkIndex) => (
                        <Typography.Link key={linkIndex} style={{ color: '#fc6706', fontSize: 13 }} onClick={() => link?.onClick?.()}>
                            <AudioOutlined /> {link.label}
                        </Typography.Link>
                    ))}
                </Space>
            )}
        </Card>
    );
};

export default ContentNavigator;
